import type { FieldError as FormFieldError, Path, UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { isApprovalByTeamEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import { pluralize } from '@gonfalon/format/pluralize';
import { AutosizeTextArea, FieldError, FormField, FormGroup } from '@gonfalon/legacy-forms/components';
import type { schemas } from '@gonfalon/openapi/types';
import clsx from 'clsx';

import { DEFAULT_AI_CONFIG_APPROVAL_SETTING } from '#components/Approvals/ApprovalSettings/ai-config/constants';
import { SelectMembersAndTeams } from '#components/Approvals/components/RequestApprovalButton/SelectMembersAndTeams';

import type { ApprovalFormSchema } from '../schemas';

import styles from '../SaveChangesModal.module.css';

type ApprovalRequestFormProps<T extends ApprovalFormSchema> = {
  form: Pick<UseFormReturn<T>, 'control' | 'formState'>;
  className?: string;
  approvalSettings?: Pick<schemas['ApprovalRequestSetting'], 'minNumApprovals'>;
};

export function ApprovalRequestForm<T extends ApprovalFormSchema>({
  form,
  className,
  approvalSettings,
}: ApprovalRequestFormProps<T>) {
  const {
    control,
    formState: { errors },
  } = form;

  const minNumApprovals = approvalSettings?.minNumApprovals ?? DEFAULT_AI_CONFIG_APPROVAL_SETTING.minNumApprovals;

  const placeholder: string = isApprovalByTeamEnabled() ? ' Select members or teams' : 'Select members';

  return (
    <div className={clsx(styles.container, className)} data-test-id="save-changes-reviewer-form">
      <FormGroup name="reason">
        <FormField isRequired label="Reason" name="reason" htmlFor="reason" hint="Provide a reason for these changes">
          <Controller<T>
            control={control}
            name={'reason' as Path<T>}
            render={({ field }) => (
              <AutosizeTextArea
                {...field}
                value={field.value as string}
                id="reason"
                rows={1}
                maxRows={4}
                data-test-id="save-changes-reason-input"
              />
            )}
          />
        </FormField>
        <FieldError
          name="reason"
          errorMessage={(errors.reason as FormFieldError)?.message}
          data-test-id="save-changes-reason-error"
        />
      </FormGroup>

      <FormGroup name="reviewers">
        <FormField
          isRequired
          label="Reviewers"
          name="reviewers"
          htmlFor="reviewers"
          hint={`Select at least ${minNumApprovals} ${pluralize(minNumApprovals, 'reviewer', 'reviewers')} to be notified.`}
        >
          <Controller<T>
            control={control}
            name={'reviewers' as Path<T>}
            render={({ field: { onChange, onBlur } }) => (
              <SelectMembersAndTeams
                name="reviewers"
                onBlur={onBlur}
                onChange={onChange}
                placeholder={placeholder}
                controlStyles={{ padding: '0' }}
                valueContainerStyles={{ paddingTop: '0', paddingBottom: '0' }}
              />
            )}
          />
        </FormField>
        <FieldError
          name="reviewers"
          errorMessage={(errors.reviewers as FormFieldError)?.message}
          data-test-id="save-changes-reviewers-error"
        />
      </FormGroup>
    </div>
  );
}
